import { createApp } from 'vue';
import { easepick, RangePlugin, KbdPlugin } from '@easepick/bundle';

const CourseListApp = {
    data() {
        return {
            courses: [],
            loading: true,
            error: null,
            filters: {
                dateRange: {
                    start: null,
                    end: null
                },
                location: '',
                search: '',
                categoryTags: '',
                tag: ''
            },
            currentPage: 1,
            coursesPerPage: 9,
            datePicker: null
        }
    },
    async mounted() {
        await this.fetchCourses();
        this.initializeDatePicker();
    },
    computed: {
        courseTypes() { // Logic is kept, in case course types are reintroduced with tags being used as subtypes
            const types = [...new Set(this.courses.map(course => course.courseType).filter(Boolean))];
            return types.sort();
        },
        courseTags() {
            const tags = [...new Set(this.courses.flatMap(course => course.categoryTags || []))];
            return tags.sort();
        },
        availableTags() {
            const categoryFilteredCourses = !this.filters.categoryTags
                ? this.courses
                : this.courses.filter(course => course.categoryTags && course.categoryTags.includes(this.filters.categoryTags));

            const subCategoryTags = [...new Set(categoryFilteredCourses.flatMap(course => course.subCategoryTags || []).filter(Boolean))];
            return subCategoryTags.sort();
        },
        locations() {
            const locs = [...new Set(this.courses.flatMap(course => course.region || []))];
            return locs.sort();
        },
        filteredCourses() {
            return this.courses.filter(course => {
                const matchesDateRange = this.isInDateRange(course.startDate);
                const matchesLocation = !this.filters.location || course.region === this.filters.location;
                const matchesSearch = !this.filters.search || this.courseMatchesSearch(course, this.filters.search);
                const matchesTags = !this.filters.categoryTags || (course.categoryTags && course.categoryTags.includes(this.filters.categoryTags));
                const matchesSubCategoryTag = !this.filters.tag || (course.subCategoryTags && course.subCategoryTags.includes(this.filters.tag));
                const isNotExpired = !course.endDate || new Date(course.endDate) >= new Date();
                
                return matchesDateRange && matchesLocation && matchesSearch && matchesTags && matchesSubCategoryTag && isNotExpired;
            }).sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
            
        },
        totalPages() {
            return Math.ceil(this.filteredCourses.length / this.coursesPerPage);
        },
        paginatedCourses() {
            const start = (this.currentPage - 1) * this.coursesPerPage;
            const end = start + this.coursesPerPage;
            return this.filteredCourses.slice(start, end);
        },
        hasPrevious() {
            return this.currentPage > 1;
        },
        hasNext() {
            return this.currentPage < this.totalPages;
        },
        hasActiveFilters() { // Logic is kept, in case course types are reintroduced with tags being used as subtypes
            return !!(this.filters.dateRange.start || 
                     this.filters.location || 
                     this.filters.search || 
                     this.filters.categoryTags ||
                     this.filters.tag);
        },
        paginationPages() {
            const pages = [];
            const totalPages = this.totalPages;
            const currentPage = this.currentPage;
            
            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) {
                    pages.push({ number: i, active: i === currentPage });
                }
            } else {
                pages.push({ number: 1, active: 1 === currentPage });
                
                const window = 5;
                let start = currentPage - Math.floor(window / 2);
                start = Math.max(2, start);
                let end = start + window - 1;
                if (end > totalPages - 1) {
                    end = totalPages - 1;
                    start = Math.max(2, end - window + 1);
                }
                
                if (start > 2) {
                    pages.push({ ellipsis: true });
                }
                
                for (let i = start; i <= end; i++) {
                    pages.push({ number: i, active: i === currentPage });
                }
                
                if (end < totalPages - 1) {
                    pages.push({ ellipsis: true });
                }
                
                pages.push({ number: totalPages, active: totalPages === currentPage });
            }
            
            return pages;
        }
    },
    watch: {
        filteredCourses() {
            this.currentPage = 1;
        },
        'filters.categoryTags'() {
            if (!this.filters.categoryTags) {
                this.filters.tag = '';
                return;
            }

            if (this.filters.tag && !this.availableTags.includes(this.filters.tag)) {
                this.filters.tag = '';
            }
        },
        currentPage() {
            this.scrollToCourseList();
        }
    },
    methods: {
        async fetchCourses() {
            try {
                this.loading = true;
                const response = await fetch('/api/CourseList');
                if (!response.ok) {
                    throw new Error('Failed to fetch courses');
                }
                this.courses = await response.json();
                console.log(this.courses);
            } catch (error) {
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },
        initializeDatePicker() {
            const self = this;
            this.datePicker = new easepick.create({
                element: '#course-date-range-picker',
                css: [
                    'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                    '/css/easepick-theme.css'
                ],
                plugins: [RangePlugin, KbdPlugin],
                RangePlugin: {
                    tooltip: true,
                    locale: {
                        one: 'dag',
                        other: 'dage'
                    }
                },
                KbdPlugin: {
                    dayIndex: 2,
                },
                lang: 'da',
                format: 'DD/MM/YYYY',
                setup(picker) {
                    picker.on('select', (e) => {
                        const { start, end } = e.detail;
                        self.filters.dateRange.start = start;
                        self.filters.dateRange.end = end;
                    });
                    picker.on('show', () => {
                        setTimeout(() => {
                            const wrapper = document.querySelector('.easepick-wrapper');
                            if (wrapper) {
                                wrapper.removeAttribute('tabindex');
                                wrapper.classList.add('picker-open');
                                const allElements = wrapper.querySelectorAll('[tabindex="-1"]');
                                allElements.forEach(el => {
                                    el.removeAttribute('tabindex');
                                });
                            }
                        }, 0);
                    });
                    
                    picker.on('hide', () => {
                        const nextFocusable = document.getElementById('course-location-select');
                        
                        setTimeout(() => {
                            const wrapper = document.querySelector('.easepick-wrapper');
                            if (wrapper) {
                                wrapper.setAttribute('tabindex', '-1');
                                wrapper.classList.remove('picker-open');
                                const allFocusable = wrapper.querySelectorAll('*');
                                allFocusable.forEach(el => {
                                    el.setAttribute('tabindex', '-1');
                                });
                                
                                const triggerSpan = wrapper.querySelector('span');
                                if (triggerSpan) {
                                    triggerSpan.setAttribute('tabindex', '0');
                                }
                            }
                            
                            if (nextFocusable) {
                                nextFocusable.focus();
                            }
                        }, 50);
                    });
                    
                
                    picker.on('render', () => {
                        setTimeout(() => {
                            const wrapper = document.querySelector('.easepick-wrapper');
                            if (wrapper) {
                                const triggerSpan = wrapper.querySelector('span');
                                if (triggerSpan) {
                                    triggerSpan.setAttribute('tabindex', '0');
                                }
                                
                                wrapper.setAttribute('tabindex', '-1');
                                
                                if (!picker.isShown()) {
                                    const calendarElements = wrapper.querySelectorAll('.container, .day, .month-name, .month-button-prev, .month-button-next');
                                    calendarElements.forEach(el => {
                                        el.setAttribute('tabindex', '-1');
                                    });
                                }
                            }
                        }, 0);
                    });
                }
            });
            
            // Inject styles into shadow DOM button using correct selector
            setTimeout(() => {
                const triggerSpan = document.querySelector('#course-date-range-picker + span:not(.easepick-wrapper)');
                if (triggerSpan && triggerSpan.shadowRoot) {
                    const button = triggerSpan.shadowRoot.querySelector('button');
                    if (button) {
                        button.style.position = 'absolute';
                        button.style.inset = '0';
                        button.style.textIndent = '-99999px';
                        button.style.overflow = '-99999px';
                        button.style.cursor = 'pointer';
                        button.setAttribute('aria-label', 'Vælg dato');
                    }
                }
            }, 100);
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('da-DK', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        },
        isSameDate(dateString1, dateString2) {
            return new Date(dateString1).toDateString() === new Date(dateString2).toDateString();
        },
        isInDateRange(courseDateString) {
            if (!this.filters.dateRange.start || !this.filters.dateRange.end) {
                return true;
            }
            
            const courseDate = new Date(courseDateString);
            // Easepick returns dates at 00:00, so widen the range to cover the full start and end days
            const startDate = new Date(this.filters.dateRange.start);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(this.filters.dateRange.end);
            endDate.setHours(23, 59, 59, 999);

            return courseDate >= startDate && courseDate <= endDate;
        },
        courseMatchesSearch(course, searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            
            const nameMatch = course.name.toLowerCase().includes(lowerSearchTerm);
            const teaserMatch = course.teaser && course.teaser.toLowerCase().includes(lowerSearchTerm);
            const dateMatch = this.formatDate(course.startDate).toLowerCase().includes(lowerSearchTerm);
            
            return nameMatch || teaserMatch || dateMatch;
        },

        clearDateRange() {
            this.filters.dateRange.start = null;
            this.filters.dateRange.end = null;
            if (this.datePicker) {
                this.datePicker.clear();
            }
        },
        clearAllFilters() {
            this.filters.location = '';
            this.filters.search = '';
            this.filters.categoryTags = '';
            this.filters.tag = '';
            this.clearDateRange();
        },
        toggleTag(tag) {
            this.filters.tag = this.filters.tag === tag ? '' : tag;
        },
        scrollToCourseList() {
            const courseList = document.querySelector('#course-list-app');
            
            if (courseList) {
                const elementPosition = courseList.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - 150;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        }
    }
};

if (document.querySelector('#course-list-app')) {
    createApp(CourseListApp).mount('#course-list-app');
}